'use client'

import { useCallback, useEffect, useId, useRef, useState } from 'react'

import { Carousel } from '@systhemaui/react'

const DEFAULT_DELAY = 8000
const PIE_SIZE = 24
const PIE_HALF = PIE_SIZE / 2
const PIE_R = PIE_SIZE / 4
const PIE_CIRCUMFERENCE = 2 * Math.PI * PIE_R


interface HeroCarouselProps {
  children: React.ReactNode
  durations?: number[]
}

export function HeroCarousel({ children, durations }: HeroCarouselProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const navigationPrevRef = useRef<HTMLButtonElement>(null)
  const navigationNextRef = useRef<HTMLButtonElement>(null)
  const swiperRef = useRef<any>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const durationsRef = useRef(durations)
  useEffect(() => {
    durationsRef.current = durations
  }, [durations])
  const [animKey, setAnimKey] = useState(0)
  const [currentDelay, setCurrentDelay] = useState(durations?.[0] ?? DEFAULT_DELAY / 1000)
  const [paused, setPaused] = useState(false)
  const [dragging, setDragging] = useState(false)
  const pausedRef = useRef(false)
  const id = useId()
  const carouselId = id.replace(/[^a-zA-Z0-9]/g, '')

  const getSlideDelay = useCallback((index?: number) => {
    const idx = index ?? swiperRef.current?.realIndex ?? 0
    const seconds = durationsRef.current?.[idx] ?? DEFAULT_DELAY / 1000
    return seconds * 1000
  }, [])

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const scheduleNext = useCallback(() => {
    clearTimer()
    const delay = getSlideDelay()
    setCurrentDelay(delay / 1000)
    setAnimKey((k) => k + 1)
    timerRef.current = setTimeout(() => {
      swiperRef.current?.slideNext()
    }, delay)
  }, [clearTimer, getSlideDelay])

  const togglePause = useCallback(() => {
    if (pausedRef.current) {
      pausedRef.current = false
      setPaused(false)
      scheduleNext()
    } else {
      pausedRef.current = true
      setPaused(true)
      clearTimer()
    }
  }, [scheduleNext, clearTimer])

  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const setup = setTimeout(() => {
      const swiperEl = wrapper.querySelector('.swiper') as HTMLElement & { swiper?: any }
      if (!swiperEl?.swiper) return
      const swiper = swiperEl.swiper
      swiperRef.current = swiper

      // Wire custom navigation buttons
      if (navigationPrevRef.current && navigationNextRef.current) {
        if (swiper.params.navigation && typeof swiper.params.navigation !== 'boolean') {
          swiper.params.navigation.prevEl = navigationPrevRef.current
          swiper.params.navigation.nextEl = navigationNextRef.current
          swiper.navigation.init()
          swiper.navigation.update()
        }
      }

      // Pause while dragging
      swiper.on('touchStart', () => {
        clearTimer()
        setDragging(true)
      })
      swiper.on('touchEnd', () => {
        setDragging(false)
      })

      // Reset timer on slide change (only if not manually paused)
      swiper.on('slideChange', () => {
        setDragging(false)
        if (!pausedRef.current) {
          scheduleNext()
        }
      })

      // Start autoplay
      scheduleNext()
    }, 100)

    return () => {
      clearTimeout(setup)
      clearTimer()
    }
  }, [scheduleNext, clearTimer])

  return (
    <div ref={wrapperRef} className="relative">
      <Carousel pagination="wrapper" navigation="none" loop disableAnimation>
        {children}
      </Carousel>

      {/* Navigation controls at bottom */}
      <div className="absolute right-6 bottom-10 z-10 flex items-center gap-3">
        {/* Pie progress timer */}
        <svg
          key={animKey}
          width={PIE_SIZE}
          height={PIE_SIZE}
          viewBox={`0 0 ${PIE_SIZE} ${PIE_SIZE}`}
          className="-rotate-90"
          aria-hidden="true"
        >
          <circle cx={PIE_HALF} cy={PIE_HALF} r={PIE_HALF} fill="white" fillOpacity={0.3} />
          <circle
            cx={PIE_HALF}
            cy={PIE_HALF}
            r={PIE_R}
            fill="none"
            stroke="var(--color-orange-500, #f97316)"
            strokeWidth={PIE_HALF}
            strokeDasharray={PIE_CIRCUMFERENCE}
            strokeDashoffset={PIE_CIRCUMFERENCE}
            style={{
              animationName: paused ? 'none' : 'hero-pie-fill',
              animationDuration: `${currentDelay * 1000}ms`,
              animationTimingFunction: 'linear',
              animationFillMode: 'forwards',
              animationPlayState: dragging ? 'paused' : 'running',
            }}
          />
        </svg>

        {/* Play/Pause toggle */}
        <button
          onClick={togglePause}
          className="cursor-pointer text-white/50 hover:text-white"
          aria-label={paused ? 'Resume autoplay' : 'Pause autoplay'}
        >
          {paused ? (
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
              <path d="M320-200v-560l440 280-440 280Z" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
              <path d="M560-200v-560h160v560H560Zm-320 0v-560h160v560H240Z" />
            </svg>
          )}
        </button>

        <button
          ref={navigationPrevRef}
          className="hidden cursor-pointer text-white/50 hover:text-white md:inline-flex"
          aria-controls={`${carouselId}-swiper`}
          aria-label="Previous Slide"
        >
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
            <path d="m313-440 224 224-57 56-320-320 320-320 57 56-224 224h487v80H313Z" />
          </svg>
          <span className="sr-only!">Previous</span>
        </button>
        <button
          ref={navigationNextRef}
          className="hidden cursor-pointer text-white/50 hover:text-white md:inline-flex"
          aria-controls={`${carouselId}-swiper`}
          aria-label="Next Slide"
        >
          <span className="sr-only!">Next</span>
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
            <path d="M647-440H160v-80h487L423-744l57-56 320 320-320 320-57-56 224-224Z" />
          </svg>
        </button>
      </div>

      <style>{`
        @keyframes hero-pie-fill {
          from { stroke-dashoffset: ${PIE_CIRCUMFERENCE}; }
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  )
}
